/**
 * useDefaultThemes — persisted per-resource theme defaults.
 *
 * Stores one default TeachBudThemeId per resource type in localStorage.
 * The three resources can each have a different default.
 * Teachers may also apply the same theme to all three at once.
 */

import { useState, useCallback } from 'react';
import { type TeachBudThemeId } from './theme-engine';

type ResourceType = 'lesson' | 'activity' | 'presentation';

const STORAGE_KEYS: Record<ResourceType, string> = {
  lesson:       'teachbud.defaultTheme.lesson',
  activity:     'teachbud.defaultTheme.activity',
  presentation: 'teachbud.defaultTheme.presentation',
};

const TEACHBUD_DEFAULT: TeachBudThemeId = 'modern';

function readStored(type: ResourceType): TeachBudThemeId {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS[type]);
    if (raw) return raw as TeachBudThemeId;
  } catch { /* localStorage unavailable */ }
  return TEACHBUD_DEFAULT;
}

function writeStored(type: ResourceType, id: TeachBudThemeId) {
  try {
    localStorage.setItem(STORAGE_KEYS[type], id);
  } catch { /* quota exceeded or unavailable */ }
}

function clearStored(type: ResourceType) {
  try {
    localStorage.removeItem(STORAGE_KEYS[type]);
  } catch { /* unavailable */ }
}

/**
 * Returns the stored default for a given resource type, and helpers to
 * save, restore, and apply-to-all.
 *
 * Usage:
 *   const { savedDefault, saveDefault, restoreDefault } = useDefaultThemes('lesson');
 *   const [themeId, setThemeId] = useState<TeachBudThemeId>(savedDefault);
 */
export function useDefaultThemes(type: ResourceType) {
  const [savedDefault, setSavedDefault] = useState<TeachBudThemeId>(() => readStored(type));

  const saveDefault = useCallback((id: TeachBudThemeId) => {
    writeStored(type, id);
    setSavedDefault(id);
  }, [type]);

  const restoreDefault = useCallback(() => {
    clearStored(type);
    setSavedDefault(TEACHBUD_DEFAULT);
    return TEACHBUD_DEFAULT;
  }, [type]);

  /** Write the same id as default for all three resource types. */
  const saveAsAllDefaults = useCallback((id: TeachBudThemeId) => {
    (['lesson', 'activity', 'presentation'] as ResourceType[]).forEach(t => writeStored(t, id));
    setSavedDefault(id);
  }, []);

  return { savedDefault, saveDefault, restoreDefault, saveAsAllDefaults, TEACHBUD_DEFAULT };
}
